import { useEffect } from 'react';

import { CoreFormFieldSelect } from '@bambi/component-library/src/components/forms/fields/CoreFormFieldSelect';
import { Controller, useFormContext } from 'react-hook-form';

import { useAppSelector } from 'src/app/store/hooks';
import {
  MessageSettings,
  PickupMessageModeEnum,
  TripAcknowledgementMessageModeEnum,
} from 'src/common/external/bambi-api/messageSettingsApi';
import { ContextFormFieldSwitch } from 'src/common/FormField/v2/context/ContextFormFieldSwitch';
import { selectCurrentOrganization } from 'src/features/auth/selectors';
import { SettingsRow } from 'src/features/billing/settings/components/SettingsRow';

import { SettingsRowHeader } from '../OrganizationSettingsForm/components/SettingsRowHeader';
import {
  PUBLIC_TRIP_PORTAL_MESSAGE,
  TRIP_MESSAGE_ACKNOWLEDGEMENT,
  TRIP_MESSAGE_CANCELED,
  TRIP_MESSAGE_REMINDER_DAY_BEFORE,
  TRIP_MESSAGE_REMINDER_NEAR_PICKUP,
} from './templates';

import type { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';

const DEFAULT_SECONDS_TO_SEND_MESSAGE_BEFORE_PICKUP_TIME = 15 * 60;

const messageModeOptions: SelectOption<PickupMessageModeEnum>[] = [
  {
    label: 'Near Pickup',
    value: 'near-pickup',
  },
  {
    label: 'Before Pickup Time',
    value: 'before-pickup-time',
  },
];

const minutesOptions: SelectOption<number>[] = (() => {
  const options = [];
  for (let i = 5; i <= 60; i += 5) {
    options.push({ label: `${i} minutes`, value: i * 60 });
  }
  return options;
})();

const tripAcknowledgementMessageModeOptions: SelectOption<TripAcknowledgementMessageModeEnum>[] =
  [
    {
      label: 'Same Day',
      value: 'same-day',
    },
    {
      label: 'Any Day',
      value: 'any-day',
    },
  ];

export function MessageSettingsForm() {
  const { watch, control, setValue } = useFormContext<MessageSettings>();
  const pickupMessageHelperText =
    "When to send the pickup message: 'Near Pickup' is when the driver is within 15 minutes of the pickup location, 'Before Pickup Time' is a fixed time before the scheduled pickup time.";

  const messageMode = watch('pickup_message_mode');

  const currentOrganization = useAppSelector(selectCurrentOrganization);
  const displayName = currentOrganization?.name;
  const dispatchPhoneNumber =
    currentOrganization?.settings.dispatch_phone_number;
  const dispatchTextNumber = currentOrganization?.settings.dispatch_text_number;
  const dispatchContactNumber = dispatchTextNumber ?? dispatchPhoneNumber;

  useEffect(() => {
    if (messageMode === 'near-pickup') {
      setValue(
        'seconds_to_send_message_before_pickup_time',
        DEFAULT_SECONDS_TO_SEND_MESSAGE_BEFORE_PICKUP_TIME
      );
    }
  }, [messageMode, setValue]);

  return (
    <div className="pb-20">
      <SettingsRow>
        <SettingsRowHeader
          title="Trip Pickup Message"
          description="Timing: Sent near pickup time"
        />
        <div className="flex flex-col gap-2">
          <div>
            <ContextFormFieldSwitch
              label="Enable Pickup Message"
              fieldPath="enable_pickup_message"
            />
          </div>
          <MessageTemplate
            template={TRIP_MESSAGE_REMINDER_NEAR_PICKUP(displayName ?? '')}
          />
        </div>
        <div>
          <Controller
            control={control}
            name="pickup_message_mode"
            render={({ field }) => (
              <CoreFormFieldSelect
                label="Pickup Message Mode"
                helperText={{
                  text: pickupMessageHelperText,
                }}
                inputProps={{
                  id: 'message_mode',
                  name: 'message_mode',
                }}
                options={messageModeOptions}
                value={messageModeOptions.find(
                  (option) => option.value === field.value
                )}
                onChange={(value) => {
                  field.onChange(value.value);
                }}
              />
            )}
          />
        </div>
        <div>
          <Controller
            control={control}
            name="seconds_to_send_message_before_pickup_time"
            render={({ field }) => (
              <CoreFormFieldSelect
                disabled={messageMode !== 'before-pickup-time'}
                inputProps={{
                  id: 'seconds_to_send_message_before_pickup_time',
                  name: 'seconds_to_send_message_before_pickup_time',
                }}
                label="Minutes to Send Message Before Pickup Time"
                options={minutesOptions}
                value={minutesOptions.find(
                  (option) => option.value === field.value
                )}
                onChange={(value) => {
                  field.onChange(value.value);
                }}
              />
            )}
          />
        </div>
      </SettingsRow>
      <SettingsRow>
        <SettingsRowHeader
          title="Trip Acknowledgement Message"
          description="Timing: Sent after trip is created"
        />
        <div className="flex flex-col gap-2">
          <ContextFormFieldSwitch
            label="Enable Trip Acknowledgement Message"
            fieldPath="enable_trip_acknowledgement_message"
          />
          <MessageTemplate
            template={TRIP_MESSAGE_ACKNOWLEDGEMENT(
              displayName ?? '',
              dispatchContactNumber
            )}
          />
        </div>
        <div>
          <Controller
            control={control}
            name="trip_acknowledgement_message_mode"
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <CoreFormFieldSelect
                  inputProps={{
                    id: 'trip_acknowledgement_message_mode',
                    name: 'trip_acknowledgement_message_mode',
                  }}
                  label="Trip Acknowledgement Message Mode"
                  options={tripAcknowledgementMessageModeOptions}
                  value={tripAcknowledgementMessageModeOptions.find(
                    (option) => option.value === field.value
                  )}
                  onChange={(value) => {
                    field.onChange(value.value);
                  }}
                />
                <div className="flex flex-col gap-2 text-xs text-gray-500">
                  <div>
                    <strong>Same Day:</strong>
                    <ul>
                      <li>
                        Only send messages for trips created and scheduled for
                        today.
                      </li>
                      <li>
                        Example: If a trip is created today, and is scheduled
                        today this message will be sent. If the trip is
                        scheduled for tomorrow, this message will not be sent.
                      </li>
                    </ul>
                  </div>
                  <div>
                    <strong>Any Day:</strong>
                    <ul>
                      <li>
                        Send messages for trips created and scheduled for any
                        day.
                      </li>
                      <li>
                        Example: If a trip is created on Monday and scheduled
                        for Friday, the message will be sent when the trip is
                        created.
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          />
        </div>
      </SettingsRow>
      <SettingsRow>
        <SettingsRowHeader
          title="Trip Reminder Message"
          description="Timing: Sent before pickup time"
        />
        <div className="flex flex-col gap-2">
          <ContextFormFieldSwitch
            label="Enable Reminder Message"
            fieldPath="enable_reminder_message"
          />
          <MessageTemplate
            template={TRIP_MESSAGE_REMINDER_DAY_BEFORE(
              displayName ?? '',
              dispatchContactNumber
            )}
          />
        </div>
      </SettingsRow>
      <SettingsRow>
        <SettingsRowHeader
          title="Public Trip Portal Message"
          description="Timing: Sent after trip is created"
        />
        <div className="flex flex-col gap-2">
          <ContextFormFieldSwitch
            label="Enable Public Trip Portal Message"
            fieldPath="enable_public_trip_portal_message"
          />
          <MessageTemplate
            template={PUBLIC_TRIP_PORTAL_MESSAGE('PUBLIC_TRIP_PORTAL_URL')}
          />
        </div>
      </SettingsRow>
      <SettingsRow>
        <SettingsRowHeader
          title="Trip Canceled Message"
          description="Timing: Sent after trip is canceled"
        />
        <div className="flex flex-col gap-2">
          <ContextFormFieldSwitch
            label="Enable Trip Canceled Message"
            fieldPath="enable_trip_canceled_message"
          />
          <MessageTemplate
            template={TRIP_MESSAGE_CANCELED(
              displayName ?? '',
              dispatchContactNumber
            )}
          />
        </div>
      </SettingsRow>
    </div>
  );
}

function MessageTemplate({ template }: { template: string }) {
  return (
    <div className="text-xs">
      <strong>Template:</strong>
      <pre className="whitespace-pre-wrap">{template}</pre>
    </div>
  );
}
